import { Screen } from '@epihub/web/src/routes/Screen';
import { homeChatRoute } from '@krishanmadan/core/src/routes/frontendRoutes';
import * as ReactDOM from 'react-dom';
import * as React from 'react';
import type { PropsWithChildren } from 'react';
import { appPortal } from '@krishanmadan/frontend/src/lib/HTMLComp';
import { ChatViewMessageBoxAnimationContext } from '@krishanmadan/frontend/src/components/chat/ChatViewMessageBoxAnimationContext';
import { KeyboardContext } from '@krishanmadan/frontend/src/components/chat/KeyboardContext';
import { Header } from '@krishanmadan/frontend/src/components/Header';
import { styled } from '@krishanmadan/frontend/src/theme/theme';
import { cssFillHeightByPercentage, cssGlobal } from '@krishanmadan/frontend/src/components/css';
import { Chat } from '@krishanmadan/frontend/src/components/chat/Chat';
import { ThemedBackground } from '@krishanmadan/frontend/src/components/ThemedBackground';
import { MisuseOverlay } from '@krishanmadan/frontend/src/components/MisuseOverlay';

export const HomeChatScreen = Screen(homeChatRoute, () => {
  return (
    <MisuseOverlay>
      <div
        id='backgrounds'
        style={{
          zIndex: 0,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}
      >
        <ThemedBackground />
      </div>
      {ReactDOM.createPortal(
        (
          <Fixed id='fixed'>
            <ScrollableExternal>
              <FixedHeader>
                <StyledHeader />
              </FixedHeader>
              <ChatContainer>
                <Chat />
              </ChatContainer>
            </ScrollableExternal>
          </Fixed>
        ),
        appPortal,
      )}
    </MisuseOverlay>
  );
});

const Fixed = styled.div`
  ${cssGlobal};
  ${cssFillHeightByPercentage};
  display: flex;
  flex: 1;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
`;

const ScrollableExternal = ({ children }: PropsWithChildren) => {
  const { chatListScroller } = React.useContext(ChatViewMessageBoxAnimationContext);
  const { keyboardOpen } = React.useContext(KeyboardContext);
  return (
    <Scroll
      keyboardOpen={keyboardOpen}
    >
      <ScrollColumnNormalizer
        ref={chatListScroller?.scrollableRefExternal}
      >
        {children}
      </ScrollColumnNormalizer>
    </Scroll>
  );
};

const Scroll = styled.div<{ keyboardOpen: boolean }>`
  display: flex;
  flex: 1;
  height: 100%;
  width: 100%;
  ${({ keyboardOpen }) => (keyboardOpen
  ? `
    overflow: auto;
    flex-direction: column-reverse;
  `
  : `
  `)};
`;

const ScrollColumnNormalizer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const leftRightPadding = '3vw';
const leftRightPaddingMobile = '5vw';

// Liquid Glass effect constants for mobile
const HEADER_BLUR_INTENSITY = 12;

const FixedHeader = styled.div`
  // Mobile: fixed position with light blur effect
  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2; // Below #spring (z-index: 3) so Lottie renders on top
    padding-bottom: 24px;

    // Use pseudo-element for blur to avoid affecting Lottie
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: -1;
      backdrop-filter: blur(${HEADER_BLUR_INTENSITY}px);
      -webkit-backdrop-filter: blur(${HEADER_BLUR_INTENSITY}px);
      background: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0.06) 0%,
        rgba(255, 255, 255, 0.03) 40%,
        transparent 70%
      );
      mask-image: linear-gradient(
        to bottom,
        black 0%,
        black 50%,
        transparent 100%
      );
      -webkit-mask-image: linear-gradient(
        to bottom,
        black 0%,
        black 50%,
        transparent 100%
      );
    }
  }
`;

const StyledHeader = styled(Header)`
  margin-top: ${leftRightPadding};
  padding: 0 ${leftRightPadding};

  @media (max-width: 768px) {
    padding: 0 ${leftRightPaddingMobile};
  }
`;

const ChatContainer = styled.div`
  flex: 1;
  padding: 12px ${leftRightPadding};

  @media (max-width: 768px) {
    padding: 12px ${leftRightPaddingMobile};
  }
`;
